<template>
    <Head :title="title" :aria-label="title"/>

    <!-- Accessibility announcements for screen readers -->
    <div 
        aria-live="polite" 
        aria-atomic="true" 
        class="sr-only"
        v-if="accessibilityAnnouncement"
    >
        {{ accessibilityAnnouncement }}
    </div>

    <div ref="playerContainer">
        <ShowPlayer
            v-if="playlist.length > 1"
            ref="showPlayer"
            :podcast="podcast"
            :colors="colors"
            :start-at="startAt"
            :current-track="currentTrack"
            :playback-rate="playbackRate"
            @finalized="finalized"
            @scroll="scroll"
            @save-progress="saveProgress"
            @set-progress-if-exists="setProgressIfExists"
            @play="play"
            @pause="pause"
            @mute="mute"
            @unmute="unmute"
            @skip="skip"
            @back="back"
            @playback-rate="handlePlaybackRate"
            @cta="cta"
        />

        <EpisodePlayer
            v-else
            ref="episodePlayer"
            :podcast="podcast"
            :colors="colors"
            :start-at="startAt"
            @finalized="finalized"
            @scroll="scroll"
            @save-progress="saveProgress"
            @set-progress-if-exists="setProgressIfExists"
            @play="play"
            @pause="pause"
            @mute="mute"
            @unmute="unmute"
            @skip="skip"
            @back="back"
            @playback-rate="handlePlaybackRate"
            @cta="cta"
        />

        <Playlist
            ref="playlistComponent"
            :podcast="podcast"
            :playlist="playlist"
            :podcast-id="podcast.hash_id"
            :current-time="timeline?.currentTime"
            @play-track="loadTrack"
            @play-chapter="(time) => scrollTo(time)"
            :current-track="currentTrack"
            :is-playing="timeline?.isPlaying"
            :current-chapter="timeline?.currentChapter"
            v-if="!hidePlaylist && playlist.length > 1"
        />
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch, nextTick, onUnmounted } from "vue";
import Playlist from "./Components/Playlist.vue";
import ShowPlayer from "./Components/ShowPlayer.vue";
import EpisodePlayer from "./Components/EpisodePlayer.vue";
import { useAudioWorker } from "./useAudioWorker";
import { Head } from "@inertiajs/vue3";
import __ from "./helpers";

const props = defineProps({
    podcast: {
        type: Object,
        required: true,
    },
    colors: Object,
    startAt: {
        type: [Number, String, null],
        required: false,
    },
    shareUrl: {
        type: [String, null],
        required: false,
        default: null,
    },
    subscribeUrl: {
        type: [String, null],
        required: false,
        default: null,
    },
    logUrl: {
        type: [String, null],
        required: false,
        default: null,
    },
    hidePlaylist: {
        type: Boolean,
    },
    preview: {
        type: Boolean,
    },
});

const showPlayer = ref(null);
const episodePlayer = ref(null);
const playlistComponent = ref(null);
const playerContainer = ref(null);

const title = ref(null);

// Create a reactive timeline ref that will be updated
const timeline = ref(null);

// Accessibility announcements
const accessibilityAnnouncement = ref("");

const worker = useAudioWorker(timeline, props.logUrl, props.preview);

const currentTrack = ref(0);
const firstLoad = ref(true);
const hasPlayed = ref(false);

const playlist = computed(() => Object.values(props.podcast.episodes));
const currentEpisode = computed(() => playlist.value[currentTrack.value]);
const playbackRate = ref(1);

// Update timeline when child components are available
const updateTimeline = () => {
    if (playlist.value.length > 1) {
        timeline.value = showPlayer.value?.timeline;
    } else {
        timeline.value = episodePlayer.value?.timeline;
    }
};

const interval = 15; // seconds
let startTime = 0;
let nextLogTime = 0;

onMounted(() => {
    getCurrentInstance().appContext._translations = props.translations;

    title.value = props.podcast.title;

    loadTrack(0);
    
    // Update timeline after components are mounted
    nextTick(() => {
        updateTimeline();
    });
    
    // Add keyboard shortcuts
    document.addEventListener('keydown', handleKeyboardShortcuts);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyboardShortcuts);
});

// Watch for changes in child components and update timeline
watch([showPlayer, episodePlayer], () => {
    updateTimeline();
}, { deep: true });

// Watch for playlist changes to update timeline
watch(playlist, () => {
    nextTick(() => {
        updateTimeline();
    });
});

function scroll() {
    if (!timeline.value) return;
    
    nextLogTime = timeline.value.duration;

    setTimeout(() => {
        worker.sendTrackingData({scroll: true});

        startTime = timeline.value.currentTime;
        worker.previousSecond.value = startTime;
        nextLogTime = timeline.value.currentTime + interval;
    }, 200);
}

function scrollTo(time) {
    if (timeline.value) {
        timeline.value.seekTo(time);
    }

    saveProgress();
    scroll();
}

function finalized() {
    worker.sendTrackingData({finalized: true});

    // Move to next track if available
    if (currentTrack.value < playlist.value.length - 1) {
        loadTrack(currentTrack.value + 1);

        setTimeout(() => {
            play();
        }, 200);
    }
}

function play() {
    if (timeline.value) {
        timeline.value.play();
    }

    if (timeline.value) {
        startTime = timeline.value.currentTime;

        if (isNaN(startTime)) {
            startTime = 0;
        }

        worker.previousSecond.value = startTime;
        nextLogTime = startTime + interval;
    }

    requestAnimationFrame(checkTime);

    if (!hasPlayed.value) {
        worker.sendTrackingData({play: true});
        hasPlayed.value = true;
        announceToScreenReader(__("Started playing"));
    } else {
        worker.sendTrackingData({resume: true});
        announceToScreenReader(__("Resumed playback"));
    }
}

function pause() {
    if (timeline.value) {
        timeline.value.pause();
    }

    worker.sendTrackingData({pause: true});
    announceToScreenReader(__("Paused playback"));
}

function mute() {
    if (timeline.value) {
        timeline.value.mute();
    }

    worker.sendTrackingData({mute: true});
    announceToScreenReader(__("Audio muted"));
}

function unmute() {
    if (timeline.value) {
        timeline.value.unmute();
    }

    worker.sendTrackingData({unmute: true});
    announceToScreenReader(__("Audio unmuted"));
}

function skip(seconds) {
    if (!timeline.value) return;
    
    nextLogTime = timeline.value.duration;

    timeline.value.skip(seconds);

    setTimeout(() => {
        worker.sendTrackingData({forward: true});

        startTime = timeline.value.currentTime;
        worker.previousSecond.value = startTime;
        nextLogTime = timeline.value.currentTime + interval;
    }, 200);
}

function back(seconds) {
    if (!timeline.value) return;
    
    nextLogTime = timeline.value.duration;

    timeline.value.back(seconds);

    setTimeout(() => {
        startTime = timeline.value.currentTime;
        worker.previousSecond.value = startTime;
        nextLogTime = timeline.value.currentTime + interval;

        worker.sendTrackingData({back: true});
    }, 200);
}

function cta(ctaId) {
    worker.sendTrackingData({ cta: true }, undefined, ctaId);
}

function handlePlaybackRate(rate) {
    playbackRate.value = rate;

    if (timeline.value) {
        timeline.value.playbackRate(rate);
    }
    worker.sendTrackingData({playback: rate});
}

function loadTrack(index) {
    currentTrack.value = index;

    if (!currentEpisode.value) {
        return;
    }

    worker.episodeId.value = currentEpisode.value.hash_id;
    worker.podcastId.value = props.podcast.hash_id;
    
    // Add null check for timeline
    if (timeline.value) {
        worker.episodeDuration.value = Math.ceil(timeline.value.duration || currentEpisode.value.audio.duration);
    } else {
        worker.episodeDuration.value = Math.ceil(currentEpisode.value.audio.duration);
    }
    
    hasPlayed.value = false;

    title.value = currentEpisode.value.title;
    playbackRate.value = 1;
}

//
function saveProgress() {
    checkTime();

    const podcastId = props.podcast.hash_id;
    const episodeId = currentEpisode.value.hash_id;

    if (timeline.value) {
        window.localStorage.setItem(
            podcastId + "-" + episodeId,
            timeline.value.currentTime
        );
    }
}

//
function setProgressIfExists() {
    if (!currentEpisode.value || !timeline.value) {
        return;
    }

    const podcastId = props.podcast.hash_id;
    const episodeId = currentEpisode.value.hash_id;
    const urlStartAt = Number(props.startAt) || 0;

    if (firstLoad.value && urlStartAt > 0) {
        timeline.value.seekTo(urlStartAt);
        firstLoad.value = false;
    } else {
        const progress = window.localStorage.getItem(
            podcastId + "-" + episodeId
        );

        if (progress !== null && progress !== undefined && progress !== "undefined") {
            timeline.value.seekTo(progress);
        } else {
            timeline.value.seekTo(0);
        }
    }

    worker.previousSecond.value = timeline.value.currentTime;
    worker.episodeDuration.value = Math.ceil(timeline.value.duration);
}

function checkTime() {
    if (!timeline.value) return;
    
    if (timeline.value.currentTime >= nextLogTime && !isNaN(nextLogTime) && !isNaN(timeline.value.currentTime)) {
        if (timeline.value.isPlaying) {
            worker.sendTrackingData({}, nextLogTime);

            worker.previousSecond.value = timeline.value.currentTime;
        }

        nextLogTime = timeline.value.currentTime + interval;
    }
}

function handleKeyboardShortcuts(event) {
    // Don't handle shortcuts when typing in input fields
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
    }
    
    const target = event.target;
    const activeElement = document.activeElement;
    
    // Helper function to check if element is a specific button by aria-label
    const isSpecificButton = (element, labels) => {
        if (!element) return false;
        const button = element.tagName === 'BUTTON' ? element : element.closest('button');
        if (!button) return false;
        const ariaLabel = button.getAttribute('aria-label') || '';
        return labels.some(label => ariaLabel.includes(label));
    };
    
    switch(event.key) {
        case ' ':
            // Only activate if play/pause button is focused
            if (!isSpecificButton(activeElement, ['Play', 'Pause'])) {
                return;
            }
            event.preventDefault();
            if (timeline.value?.isPlaying) {
                pause();
            } else {
                play();
            }
            break;
        case 'ArrowRight':
            // Only activate if skip button is focused
            if (!isSpecificButton(activeElement, ['Skip'])) {
                return;
            }
            event.preventDefault();
            skip(10);
            break;
        case 'ArrowLeft':
            // Only activate if back button is focused
            if (!isSpecificButton(activeElement, ['Back'])) {
                return;
            }
            event.preventDefault();
            back(10);
            break;
        case 'ArrowUp':
            // Only activate if within player container and Episodes tab is active
            if (!playerContainer.value || !playerContainer.value.contains(activeElement)) {
                return;
            }
            // Check if Episodes tab is active in Playlist component (if component exists)
            if (playlistComponent.value) {
                const currentTab = playlistComponent.value.getCurrentTab ? playlistComponent.value.getCurrentTab() : playlistComponent.value.tab?.value;
                if (currentTab && currentTab !== 'episodes') {
                    return;
                }
            }
            event.preventDefault();
            if (currentTrack.value > 0) {
                loadTrack(currentTrack.value - 1);
                // Focus the episode button after track change
                nextTick(() => {
                    if (playlistComponent.value && playlistComponent.value.focusEpisode) {
                        playlistComponent.value.focusEpisode(currentTrack.value);
                    }
                });
            }
            break;
        case 'ArrowDown':
            // Only activate if within player container and Episodes tab is active
            if (!playerContainer.value || !playerContainer.value.contains(activeElement)) {
                return;
            }
            // Check if Episodes tab is active in Playlist component (if component exists)
            if (playlistComponent.value) {
                const currentTab = playlistComponent.value.getCurrentTab ? playlistComponent.value.getCurrentTab() : playlistComponent.value.tab?.value;
                if (currentTab && currentTab !== 'episodes') {
                    return;
                }
            }
            event.preventDefault();
            if (currentTrack.value < playlist.value.length - 1) {
                loadTrack(currentTrack.value + 1);
                // Focus the episode button after track change
                nextTick(() => {
                    if (playlistComponent.value && playlistComponent.value.focusEpisode) {
                        playlistComponent.value.focusEpisode(currentTrack.value);
                    }
                });
            }
            break;
        case 'm':
        case 'M':
            // Only activate if mute/unmute button is focused
            if (!isSpecificButton(activeElement, ['Mute', 'Unmute'])) {
                return;
            }
            event.preventDefault();
            if (timeline.value?.isMuted) {
                unmute();
            } else {
                mute();
            }
            break;
    }
}

function announceToScreenReader(message) {
    accessibilityAnnouncement.value = message;
    // Clear the message after a short delay
    setTimeout(() => {
        accessibilityAnnouncement.value = "";
    }, 1000);
}
</script>

<style scoped>
::-webkit-scrollbar {
    width: 8px;
}

/* Track */
::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 8px;
}

/* Handle */
::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.5);
    border-radius: 30px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.9);
}

/* Screen reader only content */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
